import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Task } from "@/controls/ganttAsset";
import {
	RECLASSIFIED_COLOR,
	RECLASSIFIED_COLOR_LIGHT,
	RECLASSIFIED_COLOR_LIGHTER,
	WORKORDER_COLOR,
	WORKORDER_COLOR_LIGHT,
	WORKORDER_COLOR_LIGHTER,
} from "@/shared/colors";
import { ProcessLine } from "@/controls/ganttAsset/types/public-types";
import { GanttDelay, GanttEvent, GanttWorkOrder } from "./models/types";

export const createProcessLine = (delay: GanttDelay): ProcessLine => ({
	id: `delay-${delay.DelayId}`,
	DelayId: delay.DelayId,
	ShiftId: delay.ShiftId,
	AssetId: delay.AssetId,
	name: "",
	eventCode: delay.EventCode,
	start: delay.Start,
	end: delay.End,
	type: "taskLine",
	source: delay,
	comments: null,
	styles: {
		backgroundColor: delay.Color,
		progressColor: delay.Color,
		borderWidth: 1,
		borderColor: "#707175",
	},
});

// Delays hang from the lane's first bar; a lane with delays but no bars gets an
// invisible one (BarDisplay skips a "transparent" background).
export const withDelayLines = (
	tasks: Task[],
	processLines: ProcessLine[],
	startDate: Date,
	endDate: Date,
): Task[] => {
	if (tasks.length > 0) {
		tasks[0].processLines = processLines;
		return tasks;
	}
	if (processLines.length === 0) return [];
	return [
		{
			id: "synthetic-timeline",
			start: startDate,
			end: endDate,
			processStart: startDate,
			type: "task",
			progress: 0,
			name: "",
			displayName: "",
			isDisabled: true,
			processLines,
			source: { eventCode: "D" },
			styles: {
				backgroundColor: "transparent",
				backgroundSelectedColor: "transparent",
				progressColor: "transparent",
				progressSelectedColor: "transparent",
				borderColor: "transparent",
				borderWidth: 0,
			},
		},
	];
};

// Started work orders render on the bottom band (lane 1), below the delays line.
export const createWorkOrderTask = (wo: GanttWorkOrder): Task => ({
	id: `workorder-${wo.WorkOrderId}`,
	start: wo.Start,
	end: wo.End,
	processStart: wo.Start,
	type: "task",
	lane: 1,
	progress: 100,
	name: `Work Order # ${wo.WorkOrderNumber ?? "—"}`,
	displayName: [wo.ExtendedPartNumber ?? wo.PartNumber, wo.Quantity]
		.filter(Boolean)
		.join(" · "),
	processLines: [],
	source: wo,
	styles: {
		backgroundColor: WORKORDER_COLOR_LIGHT,
		backgroundSelectedColor: WORKORDER_COLOR_LIGHTER,
		progressColor: WORKORDER_COLOR,
		borderColor: WORKORDER_COLOR,
	},
	textLeftTop: dxtToLocalServerTime(wo.Start, "HH:mm"),
	textRightTop: "",
	textLeftBottom: "",
	textRightBottom: dxtToLocalServerTime(wo.End, "HH:mm"),
});

// Reclassified bundle → violet bottom-band segment spanning the bundle,
// labeled with the part it now belongs to (drawn over the WO bar on purpose).
export const createReclassifiedTask = (event: GanttEvent): Task => ({
	id: `reclassified-${event.BundleId}`,
	start: event.Start,
	end: event.End,
	processStart: event.Start,
	type: "task",
	lane: 1,
	progress: 100,
	name: `Reclassified ${event.BundleId}`,
	displayName: `Reclassified to ${event.PartNumber ?? ""}`,
	processLines: [],
	source: event,
	styles: {
		backgroundColor: RECLASSIFIED_COLOR_LIGHT,
		backgroundSelectedColor: RECLASSIFIED_COLOR_LIGHTER,
		progressColor: RECLASSIFIED_COLOR,
		borderColor: RECLASSIFIED_COLOR,
	},
	textLeftTop: "",
	textRightTop: "",
	textLeftBottom: "",
	textRightBottom: "",
});
